/* eslint-disable camelcase */

import { Alert, JobTask, JobTaskData, JobTaskStatus } from '@root/globalTypes';
import { JobRecordStage } from '@services/jobs/types';

export type JobTaskAction = 'schedule' | 'reschedule' | 'form' | 'none';

export type JobStatus =
  | 'completed'
  | 'error'
  | 'warning'
  | 'scheduled'
  | 'default'
  | 'readyToSchedule';

export const setBackground = (status: JobTaskStatus, isSubTask?: boolean) => {
  switch (status) {
    case 'completed':
      return 'bg-task-done';
    case 'rejected':
      return 'bg-task-rejected';
    default:
      return isSubTask ? 'bg-white' : 'bg-task';
  }
};

export const variantStyle = {
  gray: 'bg-neutral-100 text-neutral-800',
  grayBorder: 'bg-white text-neutral-400 border border-neutral-100',
  grayLight: 'bg-neutral-300 text-white',
  green: 'bg-success-100 text-success-800',
  red: 'bg-danger-100 text-danger-500',
  blue: 'bg-blue-400 text-white',
  blueLight: 'bg-blue-100 text-blue-500',
  dashed: 'bg-white text-blue-400 border-blue-400 border border-dashed',
};

export const variant: Record<JobTaskStatus, keyof typeof variantStyle> = {
  scheduled: 'blueLight',
  schedule_request_sent: 'gray',
  rejected: 'red',
  completed: 'green',
  to_schedule: 'grayBorder',
  not_started: 'grayBorder',
  incomplete: 'grayLight',
  in_progress: 'blue',
  ready_to_schedule: 'dashed',
  cancelled: 'red',
  on_hold: 'gray',
  paused: 'gray',
  unconfirmed: 'gray',
  confirmed: 'blueLight',
  delivering: 'blue',
  requested: 'gray',
  request_processed: 'blueLight',
  draft: 'grayLight',
};

export const sortByProp = <T>(array: T[], prop: string, direction: 'asc' | 'desc') => {
  return array.sort((a, b) =>
    direction === 'desc'
      ? (b as Record<string, number>)[prop] - (a as Record<string, number>)[prop]
      : (a as Record<string, number>)[prop] - (b as Record<string, number>)[prop],
  );
};

export const filterDuplicates = <T>(array: T[], prop: keyof T) => {
  return array.filter((value, i, self) => i === self.findIndex((t) => t[prop] === value[prop]));
};

export const sortAlerts = (alerts: Alert[]) => {
  return alerts.sort((a, b) =>
    a.alertType < b.alertType ? -1 : a.alertType > b.alertType ? 1 : 0,
  );
};

export const getTaskAlerts = (task: JobTask): Alert[] => {
  let alerts: Alert[] = [];
  if (task?.alerts?.length) alerts = [...alerts, ...task.alerts];

  task?.subtasks?.forEach((subtask) => {
    if (subtask.alerts?.length) alerts = [...alerts, ...subtask.alerts];
  });

  return sortAlerts(alerts);
};

export const getAlertsStatus = (alerts?: Alert[]): JobStatus => {
  if (alerts?.find((alert) => ['error', 'issue'].includes(alert.alertType))) return 'error';
  if (alerts?.find((alert) => ['warning', 'global_warning'].includes(alert.alertType)))
    return 'warning';
  return 'default';
};

export const getTaskStatus = (task: unknown) => getAlertsStatus(getTaskAlerts(task as JobTask));

export const getJobStatus = (stage?: JobRecordStage, skipStageStatus?: boolean): JobStatus => {
  const alertsStatus = getAlertsStatus(stage?.alerts);

  if (!skipStageStatus) {
    if (stage?.status === 'completed') return 'completed';
    if (stage?.status === 'ready_to_schedule') return 'readyToSchedule';
    if (alertsStatus === 'default' && ['scheduled', 'in_progress'].includes(stage?.status || ''))
      return 'scheduled';
  }

  return alertsStatus;
};

export const isWithoutSchedule = (data: JobTaskData) => data.date === 'without_schedule';

export type JobCommitment = 'rejected' | 'draft' | 'waiting' | 'confirmed' | 'done';

export const JOB_COMMITMENT_ORDER: JobCommitment[] = [
  'rejected',
  'draft',
  'waiting',
  'confirmed',
  'done',
];

export const getCommitment = ({ status, schedule }: Partial<JobTask>): JobCommitment => {
  if (status === 'completed') return 'done';
  if (status === 'rejected' || schedule?.status === 'rejected') return 'rejected';
  if (status === 'draft' || schedule?.status === 'draft') return 'draft';
  if (
    ['schedule_request_sent', 'requested', 'unconfirmed'].includes(status || '') ||
    schedule?.status === 'pending'
  )
    return 'waiting';
  return 'confirmed';
};

export const getTopCommitment = (tasks: Partial<JobTask>[]) =>
  JOB_COMMITMENT_ORDER.find((level) => tasks.some((task) => getCommitment(task) === level));

export const isSchedulable = ({ scheduleType }: Partial<JobTask>) =>
  scheduleType === 'internal' || scheduleType === 'external';

export const getAction = ({
  status,
  schedule,
  taskConfirmation,
}: Partial<JobTask>): JobTaskAction => {
  if (taskConfirmation === 'form') return 'form';
  if (['to_schedule', 'ready_to_schedule', 'in_progress'].includes(status || '') && !schedule)
    return 'schedule';
  if (schedule) return 'reschedule';
  return 'none';
};
